/**
 * DeterminationComposer — the recruiter's determination form, shared verbatim
 * between the Agent console's Review queue and the lead page's AgentPanel
 * (owner ask 2026-08-04: "the agent determination needs to show up on the
 * lead's page").
 *
 * The owner's option set, as short buttons with hover explainers:
 *
 *   Get more info      → agent asks the lead for exactly what you describe
 *   Resolve flag       → clears the flag(s); NO message; course unchanged
 *   Pivot to study     → your reason is shared; conversation swaps studies
 *   Eligible later     → kind close with a concrete date + recontact task
 *   Not eligible       → kind close that RELAYS your written reason
 *   Eligible, schedule → good news + next steps (can carry the booking link)
 *   Answer only        → (question items) relay your answer, nothing else
 *
 * Submitting is the approval: the agent composes the friendly SMS and sends it
 * (except Resolve, which never texts). The write-up lands on the timeline.
 *
 * "Request records" was here until 2026-08-04 and is RETIRED (owner rule): a
 * lead found being asked for medical history by a texting agent alarming, so
 * the ask belongs to a person. Records requests live on the lead page's
 * Records card (RequestRecordsModal — text, email, resend, cancel).
 */
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import api from '../../../services/api';
import { fieldCls } from '../filters';
import { DQ_CATEGORIES } from '../../lib';
import { studyInternalLabel, type StudyOption } from '../../types';
import { Z } from '../ui';

export type DeterminationGroup = 'eligibility' | 'question' | 'screening';

export interface OutcomeOption {
  value: string;
  label: string;
  tip: string;
}

/** Short labels; the `tip` is the hover popup (owner: "on hover make sure it
 *  is a proper explanation of what that button does"). */
export const OUTCOME_OPTIONS: OutcomeOption[] = [
  {
    value: 'more_info',
    label: 'Get more info',
    tip: 'The agent asks the lead for exactly what you describe in your write-up (a specific topic or detail), warmly, one question per message. No decision is communicated.',
  },
  {
    value: 'resolve',
    label: 'Resolve flag',
    tip: 'Clears the flag(s) with NO message to the lead and no change in course. The agent keeps going exactly as it was. Use when the flagged answer turns out to be fine. Your write-up lands on the timeline.',
  },
  {
    value: 'pivot',
    label: 'Pivot to study',
    tip: 'Not a fit for this study. The agent kindly shares the reason you write, offers the study you pick, and the conversation switches to it (same thread, full memory).',
  },
  {
    value: 'not_eligible_future',
    label: 'Eligible later',
    tip: 'Not eligible right now, but could be after the date you set (a washout, a birthday, a lab recheck). The agent tells them kindly with that concrete timing and asks if we can check back; a recontact task is created for that date. Moves the lead to Not eligible (syncs to CRIO) until then.',
  },
  {
    value: 'not_eligible_reason',
    label: 'Not eligible',
    tip: 'Closes kindly WITH your reason: the agent relays the reason you write (softened, scoped to this study), adds that we get new studies all the time that may be a better fit, and invites them to apply again. Moves the lead to Not eligible (syncs to CRIO).',
  },
  {
    value: 'proceed',
    label: 'Eligible · schedule',
    tip: 'Good news: they look eligible. The agent relays the next steps you describe and can include the call-scheduling link to set up the screening visit.',
  },
  {
    value: 'info',
    label: 'Answer only',
    tip: 'The agent relays your answer in its own warm voice and the conversation simply continues. Nothing else changes.',
  },
];

/** Which buttons show for which work-item type. */
const GROUP_OUTCOMES: Record<DeterminationGroup, string[]> = {
  eligibility: ['more_info', 'pivot', 'not_eligible_future', 'not_eligible_reason', 'proceed'],
  screening: ['more_info', 'resolve', 'pivot', 'not_eligible_future', 'not_eligible_reason', 'proceed'],
  question: ['info', 'more_info', 'resolve', 'pivot', 'not_eligible_future', 'not_eligible_reason', 'proceed'],
};

/** Status labels for old + new outcome values (delivery strips, history). */
export const OUTCOME_LABEL: Record<string, string> = {
  ...Object.fromEntries(OUTCOME_OPTIONS.map((o) => [o.value, o.label])),
  not_eligible_now: 'Not eligible (no reason given)',
  // Retired 2026-08-04, but rows from before it exist — history must still read.
  request_records: 'Request records (retired)',
};

const REASON_OUTCOMES = new Set(['pivot', 'not_eligible_reason']);
const DQ_OUTCOMES = new Set(['not_eligible_reason', 'not_eligible_future']);

const SUMMARY_PLACEHOLDER: Record<string, string> = {
  more_info: 'What should the agent find out? e.g. "Ask which weight loss medication and when the last dose was." It asks for exactly this, warmly — nothing beyond it.',
  resolve: 'Why is this flag okay / what did you verify? No message is sent — this write-up just lands on the timeline as the record.',
  info: 'Write the answer or guidance for the agent to relay. It will be rephrased warmly — the substance stays yours.',
  default: 'Write up the determination: what should happen and anything the agent should convey. The lead never sees this text verbatim — the agent delivers a friendly version and this write-up lands on the timeline.',
};

export default function DeterminationComposer({
  conversationId, group, currentStudyId = null, escalationId = null,
  studies: studiesProp, onSubmitted, onError, defaultOutcome = '',
}: {
  conversationId: string;
  group: DeterminationGroup;
  currentStudyId?: string | null;
  escalationId?: string | null;
  /** Pass when the parent already has the study list (the console does); the
   *  composer fetches its own on the lead page. */
  studies?: StudyOption[];
  onSubmitted: () => void;
  onError: (msg: string) => void;
  defaultOutcome?: string;
}) {
  const [outcome, setOutcome] = useState<string>(defaultOutcome);
  const [targetStudyId, setTargetStudyId] = useState('');
  const [reason, setReason] = useState('');
  const [dqCategory, setDqCategory] = useState('');
  const [eligibleOn, setEligibleOn] = useState('');
  const [eligibleUntil, setEligibleUntil] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | 'sent' | 'resolved'>(null);

  // Self-fetch the study list only when the parent did not provide one.
  const [fetchedStudies, setFetchedStudies] = useState<StudyOption[]>([]);
  useEffect(() => {
    if (studiesProp) return undefined;
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (alive) {
          setFetchedStudies(
            (res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm'),
          );
        }
      })
      .catch(() => { /* pivot select degrades to empty */ });
    return () => { alive = false; };
  }, [studiesProp]);

  const studies = studiesProp || fetchedStudies;
  const pivotChoices = studies.filter((s) => s.id && s.id !== currentStudyId);
  const options = useMemo(
    () => GROUP_OUTCOMES[group].map((v) => OUTCOME_OPTIONS.find((o) => o.value === v)!).filter(Boolean),
    [group],
  );
  const selected = OUTCOME_OPTIONS.find((o) => o.value === outcome) || null;

  const canSubmit = Boolean(
    outcome && summary.trim() && !busy && !done
    && (outcome !== 'pivot' || (targetStudyId && reason.trim()))
    && (outcome !== 'not_eligible_reason' || reason.trim())
    && (outcome !== 'not_eligible_future' || eligibleOn),
  );

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post('/api/crm/agent/determinations', {
        conversation_id: conversationId,
        escalation_id: escalationId || undefined,
        kind: group === 'question' ? 'question' : 'eligibility',
        outcome,
        target_study_id: outcome === 'pivot' ? targetStudyId || undefined : undefined,
        pivot_reason: REASON_OUTCOMES.has(outcome) ? reason.trim() || undefined : undefined,
        dq_category: DQ_OUTCOMES.has(outcome) ? dqCategory || undefined : undefined,
        eligible_on: outcome === 'not_eligible_future' ? eligibleOn || undefined : undefined,
        eligible_until: outcome === 'not_eligible_future' ? eligibleUntil || undefined : undefined,
        summary: summary.trim(),
      });
      setDone(outcome === 'resolve' ? 'resolved' : 'sent');
      onSubmitted();
    } catch (err: any) {
      onError(`Submit determination failed: ${err?.response?.data?.error || err?.message || 'request failed'}`);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          {done === 'resolved'
            ? 'Resolved — the flag is cleared, no message was sent, and the agent continues as before.'
            : 'Submitted. The agent is composing the friendly version and will send it shortly — delivery shows in the Determinations strip and on the timeline.'}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Your determination</div>

      {/* Outcome buttons — short labels, hover popup explains each. */}
      <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {options.map((o) => (
          <div key={o.value} className="group relative">
            <button
              type="button"
              onClick={() => setOutcome(o.value)}
              aria-pressed={outcome === o.value}
              className={`w-full rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors duration-150 ${outcome === o.value
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
            >
              {o.label}
            </button>
            <div
              role="tooltip"
              className={`pointer-events-none absolute bottom-full left-1/2 ${Z.menu} mb-1.5 hidden w-60 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 text-left text-[11px] leading-relaxed text-gray-600 shadow-lg group-hover:block group-focus-within:block dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300`}
            >
              <span className="font-semibold text-gray-900 dark:text-gray-100">{o.label}.</span> {o.tip}
            </div>
          </div>
        ))}
      </div>
      {/* Tap/keyboard fallback for the hover popup (phones have no hover). */}
      {selected && <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">{selected.tip}</p>}

      {outcome === 'pivot' && (
        <>
          <select
            value={targetStudyId}
            onChange={(ev) => setTargetStudyId(ev.target.value)}
            aria-label="Pivot target study"
            className={`${fieldCls} mt-2 w-full`}
          >
            <option value="">Pick the study to offer…</option>
            {pivotChoices.map((s) => (
              <option key={s.id} value={s.id || ''}>{studyInternalLabel(s)}</option>
            ))}
          </select>
          <textarea
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            rows={2}
            maxLength={1000}
            aria-label="Reason for the switch (shared with the lead)"
            placeholder="Reason for the switch — the agent WILL share this with the lead, warmly. Eligibility reasons are fine here since you're writing them (e.g. 'this study needs participants who aren't currently on insulin')."
            className={`${fieldCls} mt-2 w-full resize-y`}
          />
          <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
            This reason goes to the lead (rephrased kindly). Keep it high-level — the agent won't add detail beyond your words.
          </p>
        </>
      )}

      {outcome === 'not_eligible_reason' && (
        <>
          <textarea
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            rows={2}
            maxLength={1000}
            aria-label="Reason shared with the lead"
            placeholder="The reason the agent will give the lead — e.g. 'the injectable medication would be exclusionary for this study'. It is delivered softly, scoped to this study, with the future-studies invitation."
            className={`${fieldCls} mt-2 w-full resize-y`}
          />
          <p className="mt-1 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
            The agent stays inside your words: no added medical detail, no numbers you didn't write, never "failed" or "disqualified".
          </p>
        </>
      )}

      {outcome === 'not_eligible_future' && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300">
            Eligible from
            <input
              type="date"
              value={eligibleOn}
              onChange={(ev) => setEligibleOn(ev.target.value)}
              className={`${fieldCls} mt-0.5 w-full`}
            />
          </label>
          <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300">
            Until (optional range end)
            <input
              type="date"
              value={eligibleUntil}
              min={eligibleOn || undefined}
              onChange={(ev) => setEligibleUntil(ev.target.value)}
              className={`${fieldCls} mt-0.5 w-full`}
            />
          </label>
          <p className="col-span-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
            The agent shares this timing with the lead and asks if we can check back; a recontact task is created for the start date.
          </p>
        </div>
      )}

      {DQ_OUTCOMES.has(outcome) && (
        <select
          value={dqCategory}
          onChange={(ev) => setDqCategory(ev.target.value)}
          aria-label="Disqualification category"
          className={`${fieldCls} mt-2 w-full`}
        >
          <option value="">Reason category: pick automatically from my write-up</option>
          {DQ_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      )}

      <textarea
        value={summary}
        onChange={(ev) => setSummary(ev.target.value)}
        rows={4}
        maxLength={4000}
        placeholder={SUMMARY_PLACEHOLDER[outcome] || SUMMARY_PLACEHOLDER.default}
        className={`${fieldCls} mt-2 w-full resize-y`}
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          {outcome === 'resolve'
            ? 'Nothing is sent to the lead — this only clears the flag.'
            : 'Sends automatically once submitted — no second approval step.'}
        </p>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:py-1.5"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {outcome === 'resolve' ? 'Resolve flag' : 'Submit & send'}
        </button>
      </div>
    </div>
  );
}
